import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { id: "results", label: "Results" },
  { id: "foundation", label: "Foundation" },
  { id: "services", label: "Services" },
  { id: "how-it-works", label: "How It Works" },
  { id: "why-fuzionlabs", label: "Why Us" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("hero");
  const location = useLocation();
  const navigate = useNavigate();
  const reduced = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") return;
    const ids = ["hero", ...navLinks.map((l) => l.id), "blog-section", "free-audit"];
    const getDocTop = (el: HTMLElement) => {
      let y = 0;
      let node: HTMLElement | null = el;
      while (node) {
        y += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      return y;
    };
    const computeActive = () => {
      const lineY = window.scrollY + window.innerHeight * 0.25;
      let current = "hero";
      // Sort ids by document position so the "last past the line" wins regardless of array order
      const sorted = ids
        .map((id) => ({ id, el: document.getElementById(id) }))
        .filter((x): x is { id: string; el: HTMLElement } => !!x.el)
        .map((x) => ({ id: x.id, y: getDocTop(x.el) }))
        .sort((a, b) => a.y - b.y);
      for (const s of sorted) {
        if (s.y <= lineY) current = s.id;
      }
      setActive(current);
    };
    computeActive();
    window.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("resize", computeActive);
    return () => {
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
    };
  }, [location.pathname]);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const linkClass = (id: string) =>
    `text-sm transition-colors ${
      active === id
        ? "text-primary font-medium"
        : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 bg-background/95 transition-shadow duration-300 ${scrolled ? "shadow-sm border-b border-border" : ""}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <a href="/" className="text-xl font-bold text-foreground tracking-tight">FuzionLabs</a>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((l) => (
            <button key={l.id} onClick={() => scrollTo(l.id)} className={linkClass(l.id)}>
              {l.label}
            </button>
          ))}
          <a href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</a>
          <button onClick={() => navigate("/audit")} className="bg-primary text-primary-foreground text-sm font-medium px-5 py-2 rounded-full hover:scale-105 transition-transform">Free Audit</button>
        </div>

        <button className="md:hidden text-foreground" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={reduced ? {} : { height: 0, opacity: 0 }}
            animate={reduced ? {} : { height: "auto", opacity: 1 }}
            exit={reduced ? {} : { height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-b border-border"
          >
            <div className="flex flex-col gap-4 px-6 py-6">
              {navLinks.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className={`text-sm text-left ${active === l.id ? "text-primary font-medium" : "text-foreground"}`}
                >
                  {l.label}
                </button>
              ))}
              <a href="/blog" className="text-sm text-foreground">Blog</a>
              <button onClick={() => { setMenuOpen(false); navigate("/audit"); }} className="bg-primary text-primary-foreground text-sm font-medium px-5 py-2 rounded-full w-full">Free Audit</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
